import React from "react";
import Link from "next/link";

import { Action, Category } from "@/constants/events";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import { useEventTracker } from "@/hooks/useEventTracker";
import { determineOrderMethod } from "@/utils/determineOrderMethod";
import { getRoundedDistance } from "@/utils/distance";
import { getMerchId } from "@/utils/getMerchId";
import { localizeText } from "@/utils/localizeText";

import Carousel from "@/components/botanic/Carousel";
import DispensaryCardPlaceholder from "@/components/botanic/DispensaryCard/DispensaryCardPlaceholder";
import NoResults from "@/components/NoResults";
import PickupReadinessDispensaryCard from "@/components/PickupReadinessDispensaryCard";
import Subtitle from "@/components/Subtitle";
import TrackImpression from "@/components/TrackImpression";

type Props = {
  dispensaries: (DispensaryV3 | Record<string, never>)[];
  title: string;
  link: string;
  linkText: string;
  page: string;
  trackCardImpression: (
    slot: number,
    dispensary: DispensaryV3,
    carouselType: string,
  ) => void;
  userId?: number;
  countryCode: string;
  subtitle?: string;
  changeLocation?: boolean;
  customNoResults?: React.ReactNode;
  className?: string;
  cardsPerSlide?: number;
};

export const DispensariesCarouselView: React.FC<Props> = ({
  dispensaries,
  title,
  link,
  linkText,
  page,
  trackCardImpression,
  userId,
  countryCode,
  subtitle,
  changeLocation = false,
  customNoResults,
  className = "",
  cardsPerSlide = 4,
}) => {
  const { publishEvent } = useEventTracker();
  const SubtitleComponent = subtitle ? (
    <Subtitle
      title={title}
      subtitle={subtitle}
      changeLocation={changeLocation}
    />
  ) : undefined;

  const localizedTextSingular = localizeText("dispensary", countryCode);
  const localizedTextPlural = localizeText("dispensaries", countryCode);

  const NoResultsComponent = customNoResults || (
    <NoResults sectionName="dispensaries">
      <p className="font-bold text-md">
        {`There are no ${localizedTextPlural} in your area yet.`}
      </p>
      <p>
        <Link
          href="/dispensaries"
          className="font-bold no-underline text-green"
        >
          Check out our {localizedTextPlural} map
        </Link>{" "}
        to find the nearest {localizedTextSingular} outside of your area.
      </p>
    </NoResults>
  );

  return (
    <Carousel
      cardsPerSlide={cardsPerSlide}
      title={<h2 className="heading--m">{title}</h2>}
      subtitle={SubtitleComponent}
      seeMoreText={linkText}
      seeMoreHref={link}
      id="dispensaries-section"
      showSeeMoreCard={true}
      noResultsDisplay={NoResultsComponent}
      seeMoreOnClick={() => {
        publishEvent({
          action: Action.click,
          category: Category.carousel,
          label: `${page} ${link.includes("delivery") ? "delivery" : "dispensaries"} carousel show all`,
        });
      }}
      className={`container ${className}`}
      seeMoreCardClass="rounded overflow-hidden shadow-low"
    >
      {dispensaries.map(
        (dispensary: DispensaryV3 | Record<string, never>, slot: number) => {
          if (dispensary.name) {
            const eventLabel = link.includes("delivery")
              ? "order delivery on leafly"
              : determineOrderMethod(
                  !!dispensary.flags.includes("pickup"),
                  !!dispensary.flags.includes("delivery"),
                  dispensary.tier,
                  dispensary.flags,
                );

            const merchandisingCampaignId = getMerchId(
              dispensary as DispensaryV3,
              link.includes("delivery")
                ? "delivery-platinum-placement"
                : "platinumplacement",
            );

            return (
              <TrackImpression
                className="h-full"
                trackingFunction={() =>
                  trackCardImpression(
                    slot,
                    dispensary as DispensaryV3,
                    link.includes("delivery") ? "delivery" : "dispensaries",
                  )
                }
                key={`dispensary-${slot}`}
              >
                <PickupReadinessDispensaryCard
                  preserveHeight
                  currentStatus={dispensary.currentStatuses?.store}
                  deal={dispensary?.mostRecentActiveDeal}
                  deliveryEnabled={!!dispensary.flags.includes("delivery")}
                  distanceMi={dispensary.distanceMi}
                  featureTier={dispensary?.tier}
                  hideCTA
                  dispensaryId={dispensary.id}
                  inStoreCartEnabled={dispensary.inStoreCartEnabled}
                  isAd={dispensary.isAd}
                  isNewDispensary={dispensary.flags.includes("new_dispensary")}
                  logoUrl={dispensary.logoUrl}
                  name={dispensary.name}
                  pickupEnabled={!!dispensary.flags.includes("pickup")}
                  preorderConfigured={
                    !!dispensary.flags.includes("preorder_configured")
                  }
                  onClick={() => {
                    publishEvent({
                      action: Action.click,
                      category: Category.dispensaryCard,
                      dispensaryDistance: getRoundedDistance(
                        dispensary.distanceMi,
                        countryCode,
                      ),
                      dispensaryId: dispensary.id,
                      label: `slot #${slot + 1}: ${eventLabel}`,
                      merchandisingCampaignId:
                        merchandisingCampaignId || undefined,
                      userId,
                    });
                  }}
                  rating={dispensary.starRating}
                  reviewCount={dispensary.reviewCount}
                  scheduleType="store"
                  showDeal={true}
                  slug={dispensary.slug}
                  tags={dispensary.flags}
                  timeZone={dispensary.timeZone}
                  data-testid="dispensaries-section-card"
                />
              </TrackImpression>
            );
          } else {
            return (
              <DispensaryCardPlaceholder
                id={`dispensary-card-placeholder-${slot}`}
                key={slot}
                hideCTA
              />
            );
          }
        },
      )}
    </Carousel>
  );
};
export default DispensariesCarouselView;
